/**
 * Central catalog of the 5 IE programs.
 * Used by:
 *  - Solutions.jsx (home — short version inside Pessoa Física / In Company tabs)
 *  - ProductPage.jsx (dedicated subpages /e-personalidade etc.)
 *  - Header dropdown navigation
 */

export const PRODUCTS = [
  {
    id: 'e-personalidade',
    slug: 'e-personalidade',
    name: 'E-Personalidade',
    icon: 'solar:user-circle-linear',
    tagline_individual: 'Diagnóstico e intervenção nos padrões automáticos de personalidade.',
    tagline_corporate: 'Programa de desenvolvimento emocional e comportamental para equipes.',
    short_individual: 'Diagnóstico e intervenção nos padrões automáticos de personalidade.',
    short_corporate: 'Programa de desenvolvimento emocional e comportamental para equipes.',
    long_individual:
      'O programa mapeia sua estrutura comportamental via Eneagrama e intervém nos padrões que limitam sua liderança, comunicação e tomada de decisão. Autoconsciência emocional, redução de reatividade e governança sobre o próprio funcionamento. Formato extensivo (32h) ou intensivo (22h). Inclui 2 mentorias individuais e garantia de 5 anos.',
    long_corporate:
      'Potencializa desempenho, maturidade emocional e qualidade relacional da equipe, impactando diretamente liderança, tomada de decisão, produtividade e clima organizacional. Metodologia validada em mais de 3.000 empresas com NPS superior a 95%. Formato extensivo: 8 encontros de 4h (32h). Formato intensivo: 3 dias presenciais (22h). Inclui kit do participante, certificação, PDI, garantia de 5 anos e 2 mentorias individuais por participante.',
    format_individual: {
      modality: 'Pessoa Física',
      duration: 'Extensivo: 32h (8 encontros) · Intensivo: 22h (3 dias)',
      included: [
        'Kit do aluno (manual + livro)',
        'Certificado de conclusão',
        'Garantia de aprendizado por 5 anos',
        'PDI – Plano de Desenvolvimento Individual',
        'Trilho de desenvolvimento do perfil mapeado',
        '2 Mentorias Individuais',
      ],
    },
    format_corporate: {
      modality: 'In Company',
      duration: 'Extensivo: 32h · Intensivo: 22h (3 dias)',
      included: [
        'Diagnóstico organizacional',
        'Kit do participante',
        'Certificação',
        'PDI por colaborador',
        'Garantia de 5 anos',
        '2 mentorias individuais por participante',
      ],
    },
    competencies: [
      { icon: 'solar:lightbulb-linear', title: 'Autoconsciência estrutural' },
      { icon: 'solar:crown-linear', title: 'Liderança consciente' },
      { icon: 'solar:chat-round-line-linear', title: 'Comunicação estratégica' },
      { icon: 'solar:target-linear', title: 'Tomada de decisão sob pressão' },
      { icon: 'solar:shield-keyhole-linear', title: 'Gestão de conflitos' },
      { icon: 'solar:users-group-rounded-linear', title: 'Inteligência relacional' },
    ],
  },
  {
    id: 'e-processual',
    slug: 'e-processual',
    name: 'E-Processual',
    icon: 'solar:graph-up-linear',
    tagline_individual: 'Eneagrama aplicado à gestão dos seus projetos e ciclos pessoais.',
    tagline_corporate: 'Intervenção técnica na gestão de projetos e ciclos organizacionais.',
    short_individual: 'Eneagrama aplicado à gestão dos seus projetos e ciclos pessoais.',
    short_corporate: 'Intervenção técnica na gestão de projetos e ciclos organizacionais.',
    long_individual:
      'O programa une diagnóstico comportamental à estrutura dos 9 pontos para conduzir seus projetos com previsibilidade e intencionalidade. Identifica os padrões automáticos que sabotam seus prazos, entregas e decisões ao longo de qualquer ciclo — profissional ou pessoal.',
    long_corporate:
      'Diagnóstico da estrutura de decisão, intervenção técnica para neutralização de escolhas passivas e estabelecimento de protocolos de gestão ativa. Desenvolve competências estratégicas na leitura de cenários, condução de equipes complexas e adaptação técnica da delegação — com impacto direto em eficiência, produtividade e previsibilidade de entrega. Formato extensivo: 12 encontros. Inclui kit do participante, metodologia de Gestão em 9 Etapas, framework de estruturação de projetos e certificação.',
    format_individual: {
      modality: 'Pessoa Física',
      duration: 'Extensivo · 12 encontros',
      included: [
        'Kit do participante',
        'Metodologia de Gestão em 9 Etapas',
        'Framework de estruturação de projetos',
        'Certificação',
      ],
    },
    format_corporate: {
      modality: 'In Company',
      duration: 'Extensivo · 12 encontros',
      included: [
        'Diagnóstico organizacional',
        'Kit do participante',
        'Metodologia de Gestão em 9 Etapas',
        'Framework de estruturação de projetos',
        'Certificação',
      ],
    },
    competencies: [
      { icon: 'solar:calendar-mark-linear', title: 'Gestão de ciclos e projetos' },
      { icon: 'solar:target-linear', title: 'Previsibilidade de entregas' },
      { icon: 'solar:users-group-rounded-linear', title: 'Alinhamento de equipe' },
      { icon: 'solar:bug-linear', title: 'Diagnóstico de sabotadores' },
      { icon: 'solar:shield-check-linear', title: 'Governança de projetos' },
    ],
  },
  {
    id: 'e-corpo',
    slug: 'e-corpo',
    name: 'E-Corpo',
    icon: 'solar:body-linear',
    tagline_individual: 'O que seu comportamento verbal não revela, seu corpo entrega.',
    tagline_corporate:
      'Diagnóstico e intervenção estrutural nos padrões de resposta física da equipe.',
    short_individual: 'O que seu comportamento verbal não revela, seu corpo entrega.',
    short_corporate:
      'Diagnóstico e intervenção estrutural nos padrões de resposta física da equipe.',
    long_individual:
      'Leitura e intervenção em padrões corporais com base na Psicologia Reichiana. O programa desenvolve presença, autoridade e capacidade de resposta sob pressão — decodificando os mecanismos de defesa registrados na sua estrutura física. O corpo armazena o padrão; o programa desativa o automatismo.',
    long_corporate:
      'Identifica e neutraliza defesas de caráter que sabotam a comunicação, a autoridade e a execução profissional. Transforma reatividade sistêmica em controle operacional, com impacto direto na precisão do atendimento, sustentação de vínculos comerciais e governança do estresse corporativo. Inclui kit do participante, metodologia de Diagnóstico Corporal, framework de protocolos de neutralização e presença, avaliação de resultados e certificação.',
    format_individual: {
      modality: 'Pessoa Física',
      duration: 'Programa intensivo presencial',
      included: [
        'Kit completo do participante (Manual E-Corpo)',
        'Aplicação da metodologia de Diagnóstico Corporal',
        'Framework de protocolos de neutralização e presença',
        'Avaliação de resultados',
        'Certificação',
      ],
    },
    format_corporate: {
      modality: 'In Company',
      duration: 'Programa intensivo presencial',
      included: [
        'Diagnóstico corporal organizacional',
        'Kit do participante',
        'Framework de protocolos de neutralização',
        'Avaliação de resultados',
        'Certificação',
      ],
    },
    competencies: [
      { icon: 'solar:crown-linear', title: 'Presença e autoridade' },
      { icon: 'solar:eye-scan-linear', title: 'Leitura corporal de padrões' },
      { icon: 'solar:shield-keyhole-linear', title: 'Neutralização de defesas' },
      { icon: 'solar:bolt-circle-linear', title: 'Resposta sob pressão' },
      { icon: 'solar:medal-ribbons-star-linear', title: 'Posicionamento de autoridade' },
    ],
  },
  {
    id: 'e-vendas',
    slug: 'e-vendas',
    name: 'E-Vendas',
    icon: 'solar:chart-linear',
    tagline_individual: 'Engenharia comportamental para acelerar sua performance comercial.',
    tagline_corporate: 'Evolução estrutural na performance comercial da equipe.',
    short_individual: 'Engenharia comportamental para acelerar sua performance comercial.',
    short_corporate: 'Evolução estrutural na performance comercial da equipe.',
    long_individual:
      'O programa usa o mapeamento de personalidades para acelerar confiança, adaptar sua comunicação e elevar conversão em vendas consultivas e high-ticket. Leitura comportamental do cliente, negociação consciente e gestão de objeções por perfil. 9 encontros de 4h ou 3 dias intensivos.',
    long_corporate:
      'Diagnóstico comercial e comportamental, aplicação técnica na abordagem de vendas e gestão da evolução por indicadores. Foco em padronização de processo, qualificação da execução e governança do pipeline. Formato extensivo: 9 encontros de 4h (36h). Formato intensivo: 3 dias presenciais (22h). Inclui kit do participante, técnica SPIN adaptada ao Eneagrama, framework exclusivo PGP-Vendas, avaliação de resultados e certificação.',
    format_individual: {
      modality: 'Pessoa Física',
      duration: 'Extensivo: 36h · Intensivo: 22h (3 dias)',
      included: [
        'Kit completo do participante (manual + slides)',
        'Aplicação da técnica SPIN adaptada ao Eneagrama',
        'Framework exclusivo PGP-Vendas',
        'Avaliação de resultados',
        'Certificação',
      ],
    },
    format_corporate: {
      modality: 'In Company',
      duration: 'Extensivo: 36h · Intensivo: 22h (3 dias)',
      included: [
        'Diagnóstico comercial e comportamental',
        'Kit do participante',
        'Técnica SPIN adaptada ao Eneagrama',
        'Framework exclusivo PGP-Vendas',
        'Avaliação de resultados',
        'Certificação',
      ],
    },
    competencies: [
      { icon: 'solar:eye-scan-linear', title: 'Leitura comportamental do cliente' },
      { icon: 'solar:chat-round-line-linear', title: 'Adaptação de comunicação' },
      { icon: 'solar:handshake-linear', title: 'Negociação consciente' },
      { icon: 'solar:shield-keyhole-linear', title: 'Gestão de objeções por perfil' },
      { icon: 'solar:graph-up-linear', title: 'Governança de pipeline' },
      { icon: 'solar:cup-star-linear', title: 'Conversão high-ticket' },
    ],
  },
  {
    id: 'e-got',
    slug: 'e-got',
    name: 'E-GOT',
    icon: 'solar:medal-ribbons-star-linear',
    tagline_individual: 'Simulação estratégica imersiva que expõe seus padrões sob pressão extrema.',
    tagline_corporate: 'Laboratório comportamental para alta liderança sob pressão extrema.',
    short_individual: 'Simulação estratégica imersiva que expõe seus padrões sob pressão extrema.',
    short_corporate: 'Laboratório comportamental para alta liderança sob pressão extrema.',
    long_individual:
      'Dispositivo de aprendizagem vivencial de alto impacto. Você será exposto a cenários de poder, conflito, alianças e escassez de recursos — revelando em tempo real como seus padrões operam sob pressão. Transforma reatividade inconsciente em inteligência estratégica. Turmas exclusivas de 12 a 30 eneagramados.',
    long_corporate:
      'Simulação estratégica imersiva que expõe os líderes a cenários de poder, conflito, alianças e escassez de recursos. Diagnostica estratégias de ataque, defesa e manipulação de cada líder, substituindo o automatismo por escolhas governadas. Imersão intensiva de 1 ou 2 dias presenciais. Turmas exclusivas de 12 a 30 líderes/gestores. Inclui simulação com tabuleiro, mapeamento comportamental, material de apoio, debriefing corporativo e plano de ação.',
    format_individual: {
      modality: 'Pessoa Física',
      duration: 'Imersão presencial · 1 ou 2 dias',
      included: [
        'Simulação Estratégica Completa',
        'Mapeamento Comportamental',
        'Material de Apoio',
        'Debriefing e plano de ação',
      ],
    },
    format_corporate: {
      modality: 'In Company',
      duration: 'Imersão presencial · 1 ou 2 dias',
      included: [
        'Simulação Estratégica Completa com tabuleiro',
        'Mapeamento Comportamental de padrões',
        'Material de apoio e ferramentas de autogestão',
        'Debriefing Corporativo e plano de ação',
      ],
    },
    competencies: [
      { icon: 'solar:map-linear', title: 'Leitura de cenário' },
      { icon: 'solar:crown-linear', title: 'Governança sob pressão' },
      { icon: 'solar:shield-keyhole-linear', title: 'Gestão de conflitos estratégicos' },
      { icon: 'solar:medal-ribbons-star-linear', title: 'Posicionamento de autoridade' },
      { icon: 'solar:users-group-rounded-linear', title: 'Formação de alianças' },
      { icon: 'solar:lightbulb-linear', title: 'Inteligência decisória' },
    ],
  },
];

export const PRODUCTS_BY_SLUG = Object.fromEntries(PRODUCTS.map((p) => [p.slug, p]));
